import { sleep } from "verdi-common/utils/timeoutUtils";
import { EventHandler } from "../HelperFunctions/index.js";
import { RepeatingIrrigationEvent } from "./Scheduling/RepeatingIrrigationEvent.js";
import { IrrigationEventProto } from "./Scheduling/IrrigationEventProto.js";
import { Zone } from "./Zone.js";
import { SensoterraMoistureSensor } from "./Devices/SensoterraMoistureSensor.js";
import { OverlayBase } from "./Overlay/OverlayBase.js";
import { IrrigationDevice } from "./Devices/IrrigationDevice.js";
import { BlockValve } from "./Devices/BlockValve.js";
import { NotImplementedError } from "../Errors/BasicErrors.js";
import { AreaOfInterest } from "./AreaOfInterest.js";
import { CommandGroup } from "./Scheduling/commandGroup.js";
import { IrrigationSet } from "./Scheduling/IrrigationSet.js";
import { ThirdPartyDeviceBase } from "./Devices/ThirdPartyDeviceBase.js"; //DO not remove this import
import { ClientLoraNetworkProfile } from "./ClientLoraNetworkProfile.js";
class User {}
export class SingleZoneCategory<T> {
    byIDeventually(_id: string, _maxTime?: number, _stepSize?: number): Promise<T> {
        throw NotImplementedError;
    }
    byID: Record<string, T>;
    all: [T];
    OnAdded: EventHandler;
    OnRemoved: EventHandler;
    add(_value: T): undefined {
        throw NotImplementedError;
    }
    remove(_value: T): undefined {
        throw NotImplementedError;
    }
}

/**
 * @type {{
 *  blockValve: SingleZoneCategory<BlockValve>,
 *  irrigationDevice: SingleZoneCategory<IrrigationDevice>,
 *  overlay: SingleZoneCategory<OverlayBase>,
 *  SensoterraMoisture: SingleZoneCategory<SensoterraMoistureSensor>,
 *  User: SingleZoneCategory<User>,
 *  zone: SingleZoneCategory<Zone>,
 *  irrigationEvent: SingleZoneCategory<IrrigationEventProto>,
 *  repeatingEvent: SingleZoneCategory<RepeatingIrrigationEvent>,
 *  commandGroup : SingleZoneCategory<CommandGroup>,
 *  aoi: SingleZoneCategory<AreaOfInterest>,
 * }}
 */
export const MasterIndex: {
    blockValve: SingleZoneCategory<BlockValve>;
    irrigationDevice: SingleZoneCategory<IrrigationDevice>;
    overlay: SingleZoneCategory<OverlayBase>;
    SensoterraMoisture: SingleZoneCategory<SensoterraMoistureSensor>;
    User: SingleZoneCategory<User>;
    zone: SingleZoneCategory<Zone>;
    irrigationEvent: SingleZoneCategory<IrrigationEventProto>;
    commandGroup: SingleZoneCategory<CommandGroup>;
    repeatingEvent: SingleZoneCategory<RepeatingIrrigationEvent>;
    aoi: SingleZoneCategory<AreaOfInterest>;
    NDVI: SingleZoneCategory<unknown>;
    irrigationSet: SingleZoneCategory<IrrigationSet>;
    thirdPartyDevice: SingleZoneCategory<ThirdPartyDeviceBase>;
    loraNetworkProfile: SingleZoneCategory<ClientLoraNetworkProfile>;
} = {
    blockValve: undefined,
    irrigationDevice: undefined,
    overlay: undefined,
    SensoterraMoisture: undefined,
    User: undefined,
    zone: undefined,
    irrigationEvent: undefined,
    commandGroup: undefined,
    repeatingEvent: undefined,
    NDVI: undefined,
    aoi: undefined,
    irrigationSet: undefined,
    thirdPartyDevice: undefined,
    loraNetworkProfile: undefined,
};
let ProcessingQueue = [];
let Kill = false;
export async function findEventually(location, id, maxTime = 4000, stepSize = 200) {
    let done = false;
    let result = undefined;
    let waiting = 0;
    if (stepSize > 450) {
        stepSize = 450;
    }
    while (waiting < maxTime && !done) {
        waiting += stepSize;
        if (Kill) {
            throw new Error("Session Killed");
        }
        await sleep(stepSize);
        if (MasterIndex[location] && MasterIndex[location].byID[id]) {
            result = MasterIndex[location].byID[id];
            done = true;
        }
    }
    if (done) {
        return result;
    } else {
        // console.warn(`The ${location} with id ${id} was not able to be located`)
        throw new Error("timeout");
    }
}

export function deleteEntry(category, entry) {
    if (MasterIndex[category]) {
        MasterIndex[category].remove(entry);
    }
}

export function addEntry(category, entry) {
    if (!entry.dbID) {
        throw "No ID";
    }

    if (MasterIndex[category]) {
        MasterIndex[category].add(entry);
    } else {
        MasterIndex[category] = blankEntryForCategory(category);
        MasterIndex[category].add(entry);
    }
    ProcessingQueue.push(entry);
    doProcessing();
}

export async function clearIndex() {
    Kill = true;
    const categories = Object.keys(MasterIndex);
    categories.forEach((c) => {
        if (MasterIndex[c] && MasterIndex[c].all) {
            MasterIndex[c].all.forEach((i) => {
                i.dispose();
            });
        }
        MasterIndex[c] = blankEntryForCategory(c);
    });
    ProcessingQueue = [];
    await sleep(500);
    Kill = false;
}

let Processing = false;
let processingFunction = undefined;

/**
 * Set up function to process new models as they are loaded or created
 * @param {function} pf
 */
export function setProcessingFunction(pf) {
    processingFunction = pf;
    doProcessing();
}
async function doProcessing() {
    if (Processing) {
        return;
    }
    if (!processingFunction) {
        return;
    }
    Processing = true;
    let strikes = 0;
    while (strikes < 10) {
        if (ProcessingQueue.length > 0) {
            strikes = 0;
            const target = ProcessingQueue.pop();
            processingFunction(target);
        } else {
            strikes++;
            await sleep(50 * strikes);
        }
    }
    Processing = false;
}

/**
 * generates a blank entry for the master index
 * @param {string} category
 * @return {{all: ModelBase[],
 * add: add, byIDeventually: (function(*=, *=, *=): Promise<*|undefined>),
 * OnAdded: EventHandler, byID: Object<string, ModelBase>, OnRemoved: EventHandler, remove: remove}}
 */
export function blankEntryForCategory(category) {
    return {
        byIDeventually: async (id, maxTime = 2000, stepSize = 100) => findEventually(category, id, maxTime, stepSize),
        byID: {},
        all: [],
        OnAdded: new EventHandler(),
        OnRemoved: new EventHandler(),
        add: (v) => {
            const temp = MasterIndex[category];
            if (temp.byID[v.dbID]) {
                return;
            }
            temp.byID[v.dbID] = v;
            temp.all.push(v);
            temp.OnAdded.trigger({ target: v, added: v });
        },
        remove: (v) => {
            const temp = MasterIndex[category];
            temp.byID[v.dbID] = undefined;
            if (temp.all.includes(v)) {
                temp.all.splice(temp.all.indexOf(v), 1);
            }
            temp.OnRemoved.trigger({ target: v, removed: v });
        },
    };
}
Object.keys(MasterIndex).forEach((key) => {
    MasterIndex[key] = blankEntryForCategory(key);
});
